import { CustomerDetailFragment, GuestType, ReservedResourcePhase } from 'src/api/api.generated'
import { ReservationF, ReservedResourceF } from '../types/reservation-detail.types'
import { GuestFields, IdentificationDocumentFields, OccupancyFields } from '../api/update-reserved-resource-guests.types'
import { GuestAddress, GuestIdentificationDocument, GuestOccupancy, SimpleReservedResource } from '../types/guests.types'
import {
  defaultGuestOccupancyStart,
  emptyGuestOccupancy,
  fragmentToGuestAddress,
  fragmentToIdentificationDocument,
  isGroupMemberToConsiderAsSingleGuest,
  updateGuestOccupancyGuest,
} from '../utils/guests.utils'
import { GContext, RRContext } from './reservation-context.types'
export function getGuestOccupanciesFromRR(rr: SimpleReservedResource, hotelDate: Date, isReplaceOfCheckoutGuest?: boolean): GuestOccupancy[] {
  const checkoutGuests = isReplaceOfCheckoutGuest ? rr.guestOccupancies?.filter((g) => g.phase === ReservedResourcePhase.CHECKOUT).length ?? 0 : 0
  const gCount = rr.adults + rr.children + (isReplaceOfCheckoutGuest ? checkoutGuests + 1 : 0)
  const gs: GuestOccupancy[] = (rr.guestOccupancies || []).map((g) => {
    return {
      type: g.type,
      cityTaxExoneration: g.cityTaxExoneration ?? null,
      start: g.start,
      end: g.end,
      phase: g.phase,
      guestId: g.guest.id,
      firstName: g.guest.firstName || null,
      lastName: g.guest.lastName || null,
      language: g.guest.language,
      gender: g.guest.person?.gender ?? null,
      birthDate: g.guest.person?.birthDate ?? null,
      birthAddress: fragmentToGuestAddress(g.guest.person?.birthAddress),
      address: fragmentToGuestAddress(g.guest.address),
      nationalityId: g.guest.person?.nationalityId ?? null,
      identificationDocument: fragmentToIdentificationDocument(g.guest.person?.identificationDocument),
      checkin: g.checkin ?? null,
      checkout: g.checkout ?? null,
    }
  })
  const length = gs.length
  // Add empty guest occupancies
  for (let index = length; index < gCount; index++) {
    const start = defaultGuestOccupancyStart(rr.start, rr.end, hotelDate)
    const end = rr.end
    gs.push(emptyGuestOccupancy(start, end))
  }
  return gs.sort((firstGuest, secondGuest) => {
    if (!firstGuest.start) {
      return 1
    }
    if (!secondGuest.start) {
      return -1
    }

    return firstGuest.start.getTime() - secondGuest.start.getTime()
  })
}

export function getGuestsFromReservation(reservation: ReservationF, hotelDate: Date): Map<string, GuestOccupancy[]> {
  return (reservation.reservedResources || []).reduce((p: Map<string, GuestOccupancy[]>, rr) => {
    const gs = getGuestOccupanciesFromRR(rr, hotelDate)
    p.set(rr.id, gs)
    return p
  }, new Map())
}

export function getRRContextFromReservedResource(rr: SimpleReservedResource, hotelDate: Date, reservation: ReservationF): RRContext {
  const singleGuest = (reservation.reservedResources ?? []).reduce((p, v) => p + v.adults + v.children, 0) <= 1
  const comingFromWci = rr.guestOccupancies?.some((go) => go.wciState === 'CREATED') ?? false
  const isGroup = reservation.isGroup
  const occupanciesLength = rr.guestOccupancies?.length || 0
  const guests: GContext[] = getGuestOccupanciesFromRR(rr, hotelDate)
    .map((g, i) => ({
      ...g,
      occupancyIndex: i < occupanciesLength ? i : null,
      customerShouldUpdate: false,
    }))
    .map((g) => ({
      ...g,
      type: g.occupancyIndex === null && (singleGuest || isGroupMemberToConsiderAsSingleGuest(isGroup, g, reservation)) ? GuestType.SINGLE_GUEST : g.type,
      comingFromWci: comingFromWci,
    }))
  return { reservedResourceId: rr.id, hasChanged: false, guests, start: rr.start, end: rr.end, group: isGroup, phase: rr.phase }
}

export function getContextMapFromReservation(reservation: ReservationF, hotelDate: Date) {
  const context = (reservation.reservedResources || []).reduce((p: Map<string, RRContext>, rr) => {
    const rrContext = getRRContextFromReservedResource(rr, hotelDate, reservation)
    p.set(rr.id, rrContext)
    return p
  }, new Map())

  return context
}

export function updateRRContextGuest(rr: RRContext, reservation: ReservationF, hotelDate: Date, guestIndex: number, guest: GuestOccupancy): RRContext | null {
  let initial: GuestOccupancy[] = []
  const initialRr = reservation.reservedResources?.find((rrr) => rrr.id === rr.reservedResourceId)
  if (initialRr) {
    const isReplaceOfCheckoutGuest = initialRr.guestOccupancies ? initialRr.guestOccupancies?.length <= guestIndex : false
    initial = getGuestOccupanciesFromRR(initialRr, hotelDate, isReplaceOfCheckoutGuest)
  }

  const oldGuests = rr.guests
  const oldGuest = oldGuests[guestIndex]
  if (!oldGuest) return null

  const initialGuest = initial[guestIndex]

  const newGuest: GContext = {
    comingFromWci: false,
    ...guest,
    occupancyIndex: oldGuest.occupancyIndex,
    customerShouldUpdate: customerHasChanged(guest, initialGuest),
    customerError: undefined,
  }

  const newGuests = [...oldGuests]
  newGuests[guestIndex] = newGuest

  const hasChanged = newGuests.some((ng, i) => {
    const ig = initial[i]
    if (!ig) return false
    return guestOccupancyHasChanged(ng, ig)
  })

  const typeForUndefinedGuestOccupancy = guest.type ? getOtherDefaultGuestType(guest.type) : null

  const newRr: RRContext = {
    ...rr, //
    guests: newGuests.map((g) => (g.type !== null ? g : { ...g, type: typeForUndefinedGuestOccupancy })),
    hasChanged,
    guestOccupanciesError: undefined,
  }

  return newRr
}

export function addRRContextGuest(rr: RRContext, reservedResource: SimpleReservedResource, hotelDate: Date): RRContext {
  const start = defaultGuestOccupancyStart(reservedResource.start, reservedResource.end, hotelDate)
  const end = reservedResource.end

  const newGuest: GContext = {
    comingFromWci: false,
    ...emptyGuestOccupancy(start, end),
    occupancyIndex: null,
    customerShouldUpdate: false,
    customerError: undefined,
  }

  const newGuests = [...rr.guests, newGuest]

  const newRr: RRContext = {
    ...rr, //
    guests: newGuests,
    guestOccupanciesError: undefined,
  }

  return newRr
}

export function updateRRContextGuestFromCustomerDetail(rr: RRContext, reservation: ReservationF, hotelDate: Date, guestIndex: number, customer: CustomerDetailFragment): RRContext | null {
  let initial: GuestOccupancy[] = []
  const initialRr = reservation.reservedResources?.find((rrr) => rrr.id === rr.reservedResourceId)
  if (initialRr) {
    initial = getGuestOccupanciesFromRR(initialRr, hotelDate)
  }

  const oldGuests = rr.guests
  const newGuests: GContext[] = oldGuests.map((og, i) => {
    if (i === guestIndex || og.guestId === customer.id) {
      return {
        comingFromWci: false,
        ...updateGuestOccupancyGuest(customer, og),
        occupancyIndex: og.occupancyIndex,
        customerShouldUpdate: false,
        customerError: undefined,
      }
    } else {
      return { ...og }
    }
  })

  const hasChanged = newGuests.some((ng, i) => {
    const ig = initial[i]
    if (!ig) return true

    if (ig.guestId === customer.id) {
      return false
    } else {
      return guestOccupancyHasChanged(ng, ig)
    }
  })

  const newRr: RRContext = {
    ...rr, //
    guests: newGuests,
    hasChanged,
    guestOccupanciesError: undefined,
  }

  return newRr
}

const recordGuestAddress: Record<keyof GuestAddress, true> = {
  countryId: true,
  municipalityId: true,
  province: true,
  city: true,
}

const recordGuestIdentificationDocument: Record<keyof GuestIdentificationDocument, true> = {
  documentId: true,
  number: true,
  expirationDate: true,
  countryId: true,
  city: true,
}

const recordGuestOccupancy: Record<keyof GuestOccupancy, true> = {
  type: true,
  cityTaxExoneration: true,
  start: true,
  end: true,
  phase: true,
  guestId: true,
  firstName: true,
  language: true,
  lastName: true,
  gender: true,
  birthDate: true,
  birthAddress: true,
  address: true,
  nationalityId: true,
  identificationDocument: true,
  checkin: true,
  checkout: true,
}

function replacerGuestOccupancy(key: string, value: any): any {
  if (key === '') return value
  return recordGuestOccupancy[key as keyof GuestOccupancy] || recordGuestAddress[key as keyof GuestAddress] || recordGuestIdentificationDocument[key as keyof GuestIdentificationDocument]
    ? value
    : undefined
}

export function guestOccupancyHasChanged(g1: GuestOccupancy, g2: GuestOccupancy): boolean {
  const json1 = JSON.stringify(g1, replacerGuestOccupancy)
  const json2 = JSON.stringify(g2, replacerGuestOccupancy)
  return json1 !== json2
}

const recordCustomer: Record<keyof GuestOccupancy, boolean> = {
  type: false,
  cityTaxExoneration: false,
  start: false,
  end: false,
  phase: false,
  firstName: true,
  language: true,
  lastName: true,
  guestId: true,
  gender: true,
  birthDate: true,
  birthAddress: true,
  address: true,
  nationalityId: true,
  identificationDocument: true,
  checkin: false,
  checkout: false,
}

function replacerCustomer(key: string, value: any): any {
  if (key === '') return value
  return recordCustomer[key as keyof GuestOccupancy] || recordGuestAddress[key as keyof GuestAddress] || recordGuestIdentificationDocument[key as keyof GuestIdentificationDocument] ? value : undefined
}

function customerHasChanged(g1: GuestOccupancy, g2: GuestOccupancy): boolean {
  const json1 = JSON.stringify(g1, replacerCustomer)
  const json2 = JSON.stringify(g2, replacerCustomer)
  return json1 !== json2
}

export function contextToGuestFields(c: GContext): GuestFields | null {
  const firstName = c.firstName
  if (firstName === null) return null
  const lastName = c.lastName
  if (lastName === null) return null

  let identificationDocument: IdentificationDocumentFields | null = null
  const documentId = c.identificationDocument?.documentId
  const number = c.identificationDocument?.number
  const expirationDate = c.identificationDocument?.expirationDate
  const countryId = c.identificationDocument?.countryId
  const city = c.identificationDocument?.city ?? null

  if (documentId && number && expirationDate && countryId) {
    identificationDocument = { ...{ documentId, number, expirationDate, countryId, city } }
  }

  return {
    language: c.language || 'it',
    firstName: firstName,
    lastName: lastName,
    gender: c.gender,
    birthDate: c.birthDate,
    birthAddress: c.birthAddress,
    address: c.address,
    nationalityId: c.nationalityId,
    identificationDocument,
  }
}

export function contextToOccupancyFields(c: GContext, hotelDate: Date, reservedResource: ReservedResourceF): OccupancyFields | null {
  const type = c.type
  if (type === null) return null
  if ((c.start && !c.end) || (!c.start && c.end)) return null

  const start = c.start //
    ? c.start
    : defaultGuestOccupancyStart(reservedResource.start, reservedResource.end, hotelDate)

  const end = c.end //
    ? c.end
    : reservedResource.end

  return {
    type,
    cityTaxExoneration: c.cityTaxExoneration,
    start,
    end,
  }
}

function getOtherDefaultGuestType(guestType: GuestType): GuestType | null {
  switch (guestType) {
    case 'HEAD_OF_FAMILY':
      return 'FAMILY'
    case 'GROUP_HEAD':
      return 'GROUP_MEMBER'
    case 'SINGLE_GUEST':
      return 'SINGLE_GUEST'
    default:
      return null
  }
}
